import React, { useMemo, useRef } from 'react';

import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import Skeleton from '@atlaskit/skeleton';
import { MODAL_HEIGHT, MODAL_WIDTH } from '@atlassian/agent-studio-skills/constants';
import {
	type AgentStudioSkillsCapability,
	agentStudioSkillsEntrypoint,
} from '@atlassian/agent-studio-skills/entrypoint';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';
import type { Skill } from '@atlassian/rovo-platform-types/skills';
import { useRovoSkillsInvalidation } from '@atlassian/rovo-skills/services/rovo-skills-config-provider';

import { MenuItem } from './menu-item';

export type SkillsModalMenuItemProps = {
	cloudId: string;
	icon: React.ReactNode;
	isDisabled: boolean;
	label: string;
	onClick: () => void;
	targetPromptDraftAndModeKey?: string;
};

const MODAL_PROPS = {
	width: MODAL_WIDTH,
	height: MODAL_HEIGHT,
};

const SkillsModalFallback = (): React.JSX.Element => (
	<>
		<ModalHeader hasCloseButton>
			<ModalTitle>
				<Skeleton width="100px" height="16px" isShimmering />
			</ModalTitle>
		</ModalHeader>
		<ModalBody>
			<Inline space="space.400">
				<Skeleton width="260px" height="400px" isShimmering />
				<Stack space="space.200" grow="fill">
					<Skeleton width="100%" height="128px" isShimmering />
					<Skeleton width="100%" height="128px" isShimmering />
				</Stack>
			</Inline>
		</ModalBody>
		<UFOLoadHold name="chat-input-skills-modal-fallback" />
	</>
);

export const SkillsModalMenuItem = ({
	cloudId,
	icon,
	isDisabled,
	label,
	onClick,
	targetPromptDraftAndModeKey,
}: SkillsModalMenuItemProps): React.JSX.Element => {
	const publishSkillInsert = usePublish('ai-mate-chat-inserts');
	const { invalidateSkillLibrary } = useRovoSkillsInvalidation();
	const capabilities = useMemo<AgentStudioSkillsCapability[]>(() => {
		const isCustomSkillsM1Enabled = fg('studio_custom_skills_m1_frontend');
		const isDeleteCustomSkillEnabled = fg('rovo_chat_custom_skill_actions');

		return isCustomSkillsM1Enabled && isDeleteCustomSkillEnabled ? ['delete', 'share'] : ['share'];
	}, []);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const modalProps = useMemo(() => ({ ...MODAL_PROPS, autoFocus: searchInputRef }), []);

	const entryPointParams = useMemo(
		() => ({
			capabilities,
			cloudId,
			onSkillDeleted: invalidateSkillLibrary,
			onSkillClick: (skill: Skill) => {
				publishSkillInsert({
					type: 'insert-skill-into-prompt-input',
					source: 'skillsInputMenu',
					openChat: false,
					data: {
						...(targetPromptDraftAndModeKey ? { targetPromptDraftAndModeKey } : {}),
						skill: {
							id: skill.id,
							name: skill.name,
							slug: skill.slug,
							description: skill.description,
							iconName: skill.iconName,
							color: skill.color,
						},
						skillSelectionSource: 'skillsModal',
					},
				});
			},
		}),
		[
			capabilities,
			cloudId,
			invalidateSkillLibrary,
			publishSkillInsert,
			targetPromptDraftAndModeKey,
		],
	);

	if (isDisabled) {
		return <MenuItem icon={icon} label={label} isDisabled onClick={onClick} />;
	}

	return (
		<ModalTrigger
			entryPoint={agentStudioSkillsEntrypoint}
			entryPointParams={entryPointParams}
			entryPointProps={{ cloudId, searchInputRef }}
			modalProps={modalProps}
			Fallback={SkillsModalFallback}
		>
			{({ ref }) => (
				<MenuItem
					ref={ref as React.Ref<HTMLButtonElement>}
					icon={icon}
					label={label}
					isDisabled={false}
					onClick={onClick}
				/>
			)}
		</ModalTrigger>
	);
};
